import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "components/ui/toast";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { createCustomerGroups, customerGroupKeys, customersKeys, updateCustomerGroups } from "core";
import utilsService from "core/services/utils-service/utils.service";
import { useQueryClient } from "@tanstack/react-query";

type RouteParams = { id?: string; name?: string };
type LeadsGroup = { groupName: string };

export default function LeadsGroupForm() {
    const qc = useQueryClient();
    const navigate = useNavigate();
    const params = useParams<RouteParams>();
    const [searchParams] = useSearchParams();

    const [leadsGroups, setLeadsGroups] = useState<LeadsGroup[]>([]);
    const [leadsGroupsId, setLeadsGroupsId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    // add blank group
    const addLeadsGroup = () => {
        setLeadsGroups((prev) => [...prev, { groupName: "" }]);
    };

    // delete group
    const onDelete = (idx: number) => {
        setLeadsGroups((prev) => prev.filter((_, i) => i !== idx));
    };

    // navigate back
    const navigateTo = useCallback(() => {
        const currentPage = Number(searchParams.get("leadsGroupPage") || "1");
        navigate({
            pathname: "/leads",
            search: `?leadsGroupPage=${currentPage}`,
        });
    }, [navigate, searchParams]);

    // submit handler
    const onSubmit = async (e?: React.FormEvent) => {
        e?.preventDefault();
        if (!leadsGroups.length) return;

        // validation: empty names
        const invalid = leadsGroups.some((g) => !g.groupName.trim());
        if (invalid) {
            toast.error("Group Name is required");
            return;
        }

        // validation: duplicate names in the same form
        const names = leadsGroups.map((g) => g.groupName.trim().toLowerCase());
        const hasDuplicates = new Set(names).size !== names.length;
        if (hasDuplicates) {
            toast.error("Duplicate Group Names are not allowed");
            return;
        }

        setIsLoading(true);
        try {
            if (leadsGroupsId) {
                // update single group
                const name = leadsGroups[0].groupName.trim();
                if (!utilsService.checkLeadsName(name, [])) {
                    toast.error("Group Name can't be duplicate");
                    return;
                }
                await updateCustomerGroups({
                    id: leadsGroupsId,
                    name,
                    status: "ACTIVE",
                });
                toast.success("Updated successfully!");
            } else {
                // create multiple groups
                const validGroups = leadsGroups.filter((g) => g.groupName.trim());
                await createCustomerGroups(validGroups);
                toast.success("Created successfully!");
            }
            await Promise.all([
                qc.invalidateQueries({ queryKey: customersKeys.root }),
                qc.invalidateQueries({ queryKey: customerGroupKeys.root }),
            ]);
            navigateTo();
        } catch (err) {
            toast.error("Something went wrong!");
        } finally {
            setIsLoading(false);
        }
    };

    // lifecycle init (edit mode)
    useEffect(() => {
        if (params.id && params.id !== "new") {
            setLeadsGroupsId(params.id);
            setLeadsGroups([{ groupName: params.name || "" }]);
        }
    }, [params]);

    return (
        <div className="container-responsive main-content-container px-4 pb-4">
            {/* Header */}
            <div className="flex items-center py-4">
                <h3 className="page-title flex items-center gap-2">
                    <button
                        onClick={navigateTo}
                        className="btn-ghost flex items-center"
                        aria-label="Back"
                    >
                        ←
                    </button>
                    {leadsGroupsId ? "Update" : "Add"} Leads Group
                </h3>
                <div className="spacer flex-1" />
                {!leadsGroupsId && (
                    <Button className="rounded-2xl" onClick={addLeadsGroup}>
                        + Add
                    </Button>
                )}
            </div>

            {/* Groups card list */}
            <div className="custom-new-card p-5 card">
                {leadsGroups.length ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {leadsGroups.map((item, idx) => (
                            <div
                                key={idx}
                                className="merko-prod-leads_group_card relative"
                            >
                                {!leadsGroupsId && (
                                    <button
                                        type="button"
                                        onClick={() => onDelete(idx)}
                                        className="absolute top-2 right-2 text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300"
                                    >
                                        ✕
                                    </button>
                                )}

                                <form onSubmit={onSubmit}>
                                    <div className="mb-3">
                                        <label
                                            htmlFor={`group-name-${idx}`}
                                            className="block text-sm font-medium text-gray-700 dark:text-gray-200"
                                        >
                                            Group Name
                                        </label>
                                        <Input
                                            id={`group-name-${idx}`}
                                            className="mt-2"
                                            value={item.groupName}
                                            placeholder="Enter group name"
                                            required
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setLeadsGroups((prev) =>
                                                    prev.map((g, i) =>
                                                        i === idx ? { ...g, groupName: val } : g
                                                    )
                                                );
                                            }}
                                        />
                                    </div>
                                </form>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="py-8 text-center text-content-muted">
                        No Leads group found
                    </div>
                )}

                {/* Submit button */}
                {leadsGroups.length > 0 && (
                    <div className="mt-4 flex justify-end">
                        <Button onClick={onSubmit} loading={isLoading}>
                            {leadsGroupsId ? "Update" : "Submit"}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
